import CategoryIcon from '@/components/cashflow/CategoryIcon';
import { useDrawer } from '@/components/common/DrawerManager';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { toDollars } from '@/util/currency';
import { Box, BoxProps, Tooltip } from '@chakra-ui/react';
import {
    CashflowTransactionEntry,
    getMerchantName,
} from '@common/react/cashflow';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import { dt } from '@common/util/dateTime';
import { hoverStyleProps } from '../../common/styleHelpers';
import { Highlight, Text } from '../../common/Typography';
import InstitutionLogo from '../InstitutionLogo';
import { showTransactionEntryDetailsModal } from './TransactionEntryDetailsModal';

const TransactionEntryItem = ({
    transactionEntry,
    hoverColor = 'white',
    ...props
}: BoxProps & {
    transactionEntry: CashflowTransactionEntry;
    hoverColor?: BoxProps['color'];
}) => {
    const { showDrawer } = useDrawer();
    const currentUser = useCurrentUser();

    const avatarBadgeProps = {
        isVisible: !isSingleplayer(currentUser),
        isMine: transactionEntry.financialEntity.isMine,
        isJoint: !!transactionEntry.financialEntity.jointOwnerPartnershipId,
    };

    return (
        <Box
            key={`txn-entry-item-${transactionEntry.id}`}
            data-cy="txn-entry-item"
            py={4}
            {...hoverStyleProps({
                bg: hoverColor,
                mx: -3,
                px: 3,
            })}
            onClick={() => {
                showTransactionEntryDetailsModal({
                    transactionEntry,
                    showDrawer,
                });
            }}
            {...props}
        >
            <FlexRow justify="space-between" gap={4}>
                <FlexRow gap={4} maxW="40%">
                    <Box w={8} borderRadius="full" mt={-0.5} ml={-1}>
                        <InstitutionLogo
                            financialEntity={transactionEntry.financialEntity}
                            avatarBadge={avatarBadgeProps}
                            size={5}
                            mt={1}
                        />
                    </Box>
                    <FlexCol w="100%">
                        <FlexRow align="center" gap={2}>
                            <Text
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                            >
                                {getMerchantName(transactionEntry)}
                            </Text>
                            {transactionEntry.isPending && (
                                <Highlight colorScheme="gray">
                                    Pending
                                </Highlight>
                            )}
                        </FlexRow>
                        <Text
                            as="span"
                            fontFamily="mono"
                            fontSize="sm"
                            color="textMuted"
                        >
                            {dt(transactionEntry.date).format('MMM D, YYYY')}
                        </Text>
                    </FlexCol>
                </FlexRow>
                <FlexRow align="center">
                    <Tooltip
                        label={transactionEntry.category.name}
                        placement="top"
                    >
                        <Box>
                            <CategoryIcon
                                category={transactionEntry.category}
                            />
                        </Box>
                    </Tooltip>

                    <Box w={24} textAlign="right">
                        <Text fontSize="sm" fontWeight="medium">
                            {toDollars(transactionEntry.amount, {
                                withCents: true,
                                alwaysSigned: true,
                            })}
                        </Text>
                    </Box>
                </FlexRow>
            </FlexRow>
        </Box>
    );
};

export default TransactionEntryItem;
